<template>
  <section>
    <div class="card bg-repeat-booking shadow-sm">
      <div class="card-body">
        <strong class="text-info"><i class="fas fa-repeat"></i> {{ $t('message.repeat_booking.title') }}</strong>
        <div class="row no-gutters mt-3">
          <div class="col-6 pr-2">
            <div class="exp-secondary">
              <div class="checkbox">
                <input type="radio"
                      id="bookingWithDateTime"
                      name="bookingType"
                      :value="0"
                      v-model="preBookingData.is_no_datetime_booking"/>
                <label for="bookingWithDateTime" class="mb-0 text-small">
                  <span><!-- This span is needed to create the "checkbox" element --></span>{{ $t('message.repeat_booking.choice01') }}
                </label>
              </div>
            </div>
          </div>
          <div class="col-6 pl-2">
            <div class="exp-secondary">
              <div class="checkbox">
                <input type="radio"
                      id="bookingWithoutDateTime"
                      name="bookingType"
                      :value="1"
                      v-model="preBookingData.is_no_datetime_booking"/>
                <label for="bookingWithoutDateTime" class="mb-0 text-small">
                  <span><!-- This span is needed to create the "checkbox" element --></span>{{ $t('message.repeat_booking.choice02') }}
                </label>
              </div>
            </div>
          </div>
        </div>

        <!-- Date & time selection -->
        <div class="row no-gutters mt-3" v-if="!preBookingData.is_no_datetime_booking">
          <div class="col-6 pr-2">
            <div class="form-group">
              <input type="text"
                     id="cleaningDate"
                     name="cleaningDate"
                     class="form-control -date-picker input-jumbo"
                     :class="{'form-alert': errors.has('cleaningDate')}"
                     :placeholder="$i18n.t('message.booking.booking_step.date')"
                     @change="setCleaningDate"
                     v-pikadayBooking="calendarData"
                     v-validate="'required'"
                     ref="cleaningDate"
                     readonly/>
                     <!-- data-vv-validate-on="change" -->
              <span class="far fa-calendar-alt input-text-icon"></span>
            </div>
            <div v-show="errors.has('cleaningDate')" class="text-danger text-small">
              <i class="fas fa-exclamation-triangle"></i> {{ $t('message.required_selection') }}
            </div>
          </div>
          <div class="col-6 pl-2">
            <div class="form-group">
              <div class="select-jumbo" :class="{'form-alert': errors.has('cleaningTime')}">
                <select style="background: white;" 
                        id="cleaningTime"
                        name="cleaningTime"
                        data-vv-validate-on="change"
                        v-model="preBookingData.cleaning_time"
                        v-validate="'required'">
                  <option :value="null">{{ $t('message.booking.new_booking.time') }}</option>
                  <option :value="time" v-for="time in globalState.arrTimes">{{ time }}</option>
                </select>
                <span class="far fa-clock input-text-icon"></span>
              </div>
            </div>
            <div v-show="errors.has('cleaningTime')" class="text-danger text-small">
              <i class="fas fa-exclamation-triangle"></i> {{ $t('message.required_selection') }}
            </div>
          </div>
        </div>

        <!-- Service provider selection -->
        <div class="mt-2" v-if="!preBookingData.is_no_datetime_booking && preBookingData.cleaning_date && preBookingData.cleaning_time">
          <div class="d-flex">
            <div class="flex-fill"><strong>{{ $t('message.my_reservations.cleaner_name') }}</strong></div>
            <div>
              <a href="" class="text-danger" 
                 @click.prevent="clearSelectedProfessional()" 
                 v-if="selectedProfessional">{{ $t('message.service_item.clear') }}</a>
            </div>
          </div>
          <div class="d-flex border border-divider justify-content-start align-items-center bg-white rounded p-2 mt-1">
            <div>
              <img :src="(selectedProfessional) ? selectedProfessional.picture : 'https://beneat.co/images/ic_unknown.png'" class="rounded-circle" width="40"/>
            </div>
            <div class="flex-fill pl-2">
              <span v-if="selectedProfessional">{{ selectedProfessional.name | firstName }}</span>
              <span v-else>{{ $t('message.repeat_booking.booking_any_provider') }}</span>
            </div>
            <div>
              <button class="btn btn-outline-info" 
                      @click="showModal()" 
                      v-if="preCheckoutData && preCheckoutData.user_place && preCheckoutData.user_place.latitude && preCheckoutData.user_place.longitude">
                {{ $t('message.button.change') }}
              </button>
            </div>
          </div>
          <div class="mt-2 text-small" 
               v-if="preCheckoutData && preCheckoutData.user_place && !preCheckoutData.user_place.latitude && !preCheckoutData.user_place.longitude">
            <i class="fal fa-exclamation-circle text-orange"></i> 
            <span>{{ $t('message.repeat_booking.can_select_provider_remark') }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal -->
    <sweet-modal ref="modal" overlay-theme="dark">
      <div slot="modal-title">{{ $t('message.no_datetime_booking.select_service_provider') }}</div>
      <section v-if="professionals.length > 0">
        <div v-for="professional in professionals">
          <div class="card">
            <div class="card-body text-left">
              <div class="d-flex ustify-content-start align-items-top">
                <div>
                  <professional-picture :professional="professional" :imgSize="48"/>
                </div>
                <div class="flex-fill px-3 text-left">
                  <div class="w-100">
                    <h3 class="m-0">{{ professional.name | firstName }}</h3>
                  </div>
                  <div class="mt-1 text-small">
                    <i class="fas fa-star text-warning"/> {{ Number(professional.rating).toFixed(2) }} ({{ professional.rating_qty }})
                    ·
                    <span>
                      {{ globalState.cities[professional.province_id] | multiLangName }}
                    </span>
                  </div>
                </div>
                <div>
                  {{ professional.distance | distance }}
                </div>
              </div>
              <div class="mt-2 text-small" v-if="professional.is_excellent === 1">
                <router-link to="/excellent-provider">
                  <img src="/static/img/ic_badge_excellent.png"
                        width="18"
                        v-b-popover.hover.top="$i18n.t('message.professional.excellent_provider_detail', {name: firstName(professional.name)})"
                        :title="$i18n.t('message.professional.excellent_provider_title')">
                </router-link>
                {{ $t('message.professional.excellent_provider_title') }}
              </div>
              <div class="mt-2 text-small" v-if="professional.spoken_languages.length > 0">
                <i class="fas fa-comments"></i> {{ professional.spoken_languages }}
              </div>
            </div>
            <div class="d-flex justify-content-end pr-3 pb-3">
              <div>
                <button class="btn btn-info px-3" @click="selectProfessional(professional)">
                  {{ $t('message.company_address.choose') }}
                </button>
              </div>
            </div>
          </div>
        </div>
        <div class="pt-3" v-if="canLoadMore">
          <button class="btn btn-outline-info btn-block" @click="loadMoreProfessionals()" :disabled="isLoading">
            {{ isLoading ? $t('message.button.loading') : $t('message.button.load_more_providers') }}
          </button>
        </div>
      </section>
      <section class="text-center" v-else>
        <div v-if="isLoading">
          <loader></loader>
        </div>
        <div v-else>
          {{ $t('message.professional.no_service_provider') }}
        </div>
      </section>
    </sweet-modal>
    <!-- End Modal -->
  </section>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import ProfessionalPicture from '../professional/components/ProfessionalPicture'
import Loader from '../loader/Loader'
import { SweetModal } from 'sweet-modal-vue'
import serviceHelpers from '../../helpers/serviceHelpers'
import axios from 'axios'

export default {
  inject: ['$validator'],
  components: {
    Loader,
    ProfessionalPicture,
    SweetModal
  },
  data () {
    return {
      page: 0,
      limit: 10,
      isLoading: false,
      canLoadMore: true,
      professionals: [],
      calendarData: {
        date: null,
        isToday: false,
        sameDayBookingConfig: null,
        serviceId: null,
        provinceId: null
      }
    }
  },
  computed: {
    ...mapGetters({
      globalState: 'globalVal/getState',
      order: 'orderDetail/getOrderDetail',
      preBookingData: 'booking/getPreBookingData',
      selectedProfessional: 'booking/getSelectedProfessional',
      user: 'auth/getUser'
    }),
    ...mapState('booking', ['preCheckoutData'])
  },
  watch: {
    'preBookingData.is_no_datetime_booking': function (newVal, oldVal) {
      if (newVal === 1) {
        this.clearSelectedProfessional()
      }
      // Clear selected date-time
      this.$nextTick(() => {
        if (this.$refs.cleaningDate) {
          this.$refs.cleaningDate.value = null
        }
        this.$store.commit('booking/UPDATE_PRE_BOOKING_DATA', { key: 'cleaning_date', value: null })
        this.$store.commit('booking/UPDATE_PRE_BOOKING_DATA', { key: 'cleaning_time', value: null })
      })
    },
    'preBookingData.cleaning_date': function (newVal, oldVal) {
      this.clearSelectedProfessional()
    },
    'preBookingData.cleaning_time': function (newVal, oldVal) {
      this.clearSelectedProfessional()
    }
  },
  methods: {
    firstName (name) {
      return this.$options.filters.firstName(name)
    },
    showModal () {
      this.$refs.modal.open()
      // Reset and reload professionals
      this.page = 0
      this.professionals = []
      this.fetchProfessionals()
    },
    setCleaningDate () {
      const cleaningDate = (this.$refs.cleaningDate.value) ? this.$refs.cleaningDate.value : null
      const data = { key: 'cleaning_date', value: cleaningDate }
      this.$store.commit('booking/UPDATE_PRE_BOOKING_DATA', data)
    },
    selectProfessional (professional) {
      this.$store.commit('booking/SET_SELECTED_PROFESSIONAL', professional)
      this.$refs.modal.close()
    },
    clearSelectedProfessional () {
      this.$store.commit('booking/SET_SELECTED_PROFESSIONAL', null)
    },
    loadMoreProfessionals () {
      this.page = this.page + 1
      this.fetchProfessionals()
    },
    async fetchProfessionals () {
      this.isLoading = true
      const url = '/professionals'
      const params = {
        page: this.page,
        limit: this.limit,
        user_id: this.user.id,
        service_id: this.preBookingData.service_id,
        province_id: this.preBookingData.province_id,
        work_hour: this.preBookingData.work_hour,
        cleaning_date: this.preBookingData.cleaning_date,
        cleaning_time: this.preBookingData.cleaning_time,
        latitude: this.preBookingData.latitude,
        longitude: this.preBookingData.longitude
      }
      if (this.preCheckoutData.user_place) {
        params.user_place_id = this.preCheckoutData.user_place.id
        if (this.preCheckoutData.user_place.latitude && this.preCheckoutData.user_place.longitude) {
          params.latitude = this.preCheckoutData.user_place.latitude
          params.longitude = this.preCheckoutData.user_place.longitude
        }
      }
      const { data } = await axios.get(url, { params })
      this.isLoading = false
      if (!data.error) {
        this.canLoadMore = data.professionals.length === this.limit
        if (this.page === 0) {
          this.professionals = data.professionals
        } else {
          this.professionals = this.professionals.concat(data.professionals)
        }
      }
    }
  },
  mounted () {
    // Set calendar data to support same-day booking config
    if (this.preBookingData) {
      this.calendarData.sameDayBookingConfig = serviceHelpers.formatSameDayData(this.globalState.configData.same_day_booking)
      this.calendarData.serviceId = this.preBookingData.service_id
      if (this.globalState.services[this.preBookingData.service_id].parent_id > 0) {
        this.calendarData.serviceId = this.globalState.services[this.order.service_id].parent_id
      }
      this.calendarData.provinceId = this.preBookingData.province_id
    }
  }
}
</script>

<style scoped>
  .input-text-icon {
    float: right;
    margin-right: 1rem;
    margin-top: -2.15rem;
    position: relative;
    font-size: 120%;
    z-index: 2;
    color: #9E9E9E;
  }
</style>


// WEBPACK FOOTER //
// src/components/booking/RepeatBooking.vue